import React, {useEffect, useState} from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  TextField,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {getUserTableData} from "../../userProfiles/api/actions";
import Skeleton from "@mui/material/Skeleton";
import {EditableTable} from "../../../components/EditableTable";
import {useDispatch, useSelector} from "react-redux";
import {clearApiKeyState, generatePMApiKeys, getApiKeys, updateApiKey} from "../api/actions";
import {setSnackbar} from "../../snackbar/api/snackbar";
import SearchIcon from "@mui/icons-material/Search";
import Tooltip from '@mui/material/Tooltip';
import {ContentCopy} from "@mui/icons-material";
import {makeStyles} from "@mui/styles";

// Simple status renderer using default Material-UI chip styling
const renderStatusChip = (status) => {
  // Map status values to Material-UI chip colors
  let chipColor;
  switch(status?.toLowerCase()) {
    case 'active':
      chipColor = 'success';
      break;
    case 'inactive':
      chipColor = 'warning';
      break;
    case 'revoked':
      chipColor = 'error';
      break;
    default:
      chipColor = 'default';
  }
  
  return (
    <Chip
      label={status}
      color={chipColor}
      size="small"
      style={{ margin: "0" }}
    />
  );
};

const ApiKeysTable = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [resetCache, setResetCachedData] = useState(true);
  const [focus, setFocus] = useState(false);
  const [createKeyModalOpen, setCreateKeyModalOpen] = useState(false);
  const [editKeyModalOpen, setEditKeyModalOpen] = useState(false);
  const [pmList, setPMList] = useState([]);
  const [selectedPM, setSelectedPM] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [editModalTitle, setEditModalTitle] = useState("");
  const [copied, setCopied] = useState(false);

  const user = JSON.parse(localStorage.getItem('user'));


  const dispatch = useDispatch();
  const useStyles = makeStyles(() => ({
    buttonProgress: {
      color: "white"
    }
  }));

  const classes = useStyles();


  const getApiKeysData = async () => {
    try {
      const params = {
        page,
        rowsPerPage,
        search,
        resetCache
      }
      await dispatch(getApiKeys(params));
    } catch (err) {
      dispatch(setSnackbar(true, "error", "Error Fetching Api Keys data"));
    }
  }

  useEffect(() => {
    getApiKeysData();
  }, [dispatch, search, page, rowsPerPage, resetCache]);

  const {data, isLoading, totalCount, newApiKey} = useSelector(state => state.apiKeys);
  const {externalUsers} = useSelector(state => state.userProfiles);
  const isUsersDataLoading = useSelector(state => state.userProfiles.isLoading);

  const fieldTitles = [
    "First Name",
    "Last Name",
    "Key",
    "Scopes",
    "Status",
    "Created",
    "Last Used",
    "Expiration Date",
    ""
  ];

  const fieldTypes = [
    "label",
    "label",
    "label",
    "label",
    "label", // Changed from statusTag to label since we're rendering the chip directly
    "dateLabel",
    "dateLabel",
    "dateLabel",
    "editBtn"
  ];

  const fieldNames = [
    "firstName",
    "lastName",
    "apiKey",
    "scopes",
    "statusTag",
    "createdAt",
    "lastUsedAt",
    "expirationDate",
    "userUUid"
  ];

  // Function moved outside component to prevent recreation

  const formattedData = {
    ...data,
    items: data?.items?.map(item => ({
      ...item,
      statusTag: renderStatusChip(item.status) // Render the chip directly
    }))
  };

  const handleChangeRowsPerPage = (rowsPerPage) => {
    setPage(0);
    setResetCachedData(false);
    setRowsPerPage(rowsPerPage);
  };

  const handleChangePage = (page) => {
    setResetCachedData(false);
    setPage(page);
  };

  const CurrentLookupTypeButton = () => {
    return (
      <IconButton onClick={handleSearchTargetButtonClicked} style={{width: "50px", height: "50px"}}>
        <SearchIcon style={{width: "30px", height: "30px"}}/>
      </IconButton>
    );
  }

  const handleSearchTargetButtonClicked = async () => {
    setPage(0);
    setResetCachedData(true);
    await getApiKeysData();
  }

  const handleSearchTargetChange = async (event) => {
    setSearch(event.target.value?.trim());
    setPage(0);
    setResetCachedData(true);
    await getApiKeysData();
  }

  const onEditKeyOpen = async (item) => {
    setEditKeyModalOpen(true)
    setSelectedRow(item)
    const title = item?.status === 'Active' ? 'deactivate' : 'reactivate'
    setEditModalTitle(title)
  }

  const onConfirmDeactivate = async () => {
    let expirationDate = new Date()
    expirationDate.setHours(expirationDate.getHours() + 24)
    try {
      const updateParams = {
        status: "Inactive",
        uuid: selectedRow.uuid,
        expirationDate: expirationDate,
        userId: user.id
      }
      await dispatch(updateApiKey(updateParams))
      await getApiKeysData()
      dispatch(setSnackbar(true, "success", "Deactivated!"));
      setEditKeyModalOpen(false)
    } catch (error) {
      console.log(error, 'error')
    }
  }

  const generateNewApiKey = async () => {
    try {
      await dispatch(generatePMApiKeys(selectedPM))
      await getApiKeysData();
    } catch (error) {
      dispatch(setSnackbar(true, "error", "Error occurred. Please try again."));
      console.log(error, 'Error generating new key');
    }
  }

  const handleCreateKeyModalClose = () => {
    setSelectedPM('')
    dispatch(clearApiKeyState())
    setCreateKeyModalOpen(false)
  }

  const getPMData = async () => {
    try {
      await dispatch(getUserTableData({page: 0, rowsPerPage: 10000, isLoading: true, isPM: 1}));
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    getPMData();
  }, []);

  useEffect(() => {
    const pmUsersItems = externalUsers.users.items.filter(item => item.isPM === 1)
    setPMList(pmUsersItems)
  }, [externalUsers])


  const handlePmSelect = (event) => {
    setSelectedPM(event.target.value)
  }

  const copyToClipboard = () => {
    const text = `Key: ${newApiKey?.apiKey || ""}\nSecret: ${newApiKey?.secret || ""}`;
    navigator.clipboard.writeText(text)
    .then(() => {
      setCopied(true);
    })
    .catch(err => console.error("Failed to copy: ", err));
  };


  return (
    <Card mb={6} elevation={3}>
      <CardContent>
        <Grid container alignItems="center" spacing={2}>
          <Grid item lg={12} md={12} sm={12}>
            <TextField
              id={"searchTarget"}
              label="Search For PMs"
              type={"text"}
              autoFocus={focus}
              onChange={handleSearchTargetChange}
              name={"searchTarget"}
              defaultValue={search}
              onClick={() => setFocus(true)}
              fullWidth
              autoComplete={"off"}
              my={2}
              size={"small"}
              variant={"outlined"}
              InputLabelProps={{
                shrink: true,
              }}
              onKeyPress={async (ev) => {
                if (ev.key === 'Enter') {
                  ev.preventDefault();
                  setPage(0);
                  setResetCachedData(true);
                  await getApiKeysData()
                }
              }}
              InputProps={{endAdornment: <CurrentLookupTypeButton/>}}
              helperText="Search by first name, last name or email"
            >
            </TextField>
          </Grid>
          <Grid item lg={12} md={12} sm={12}>
            <EditableTable
              readOnly={true}
              canAddItems={true}
              canSave={false}
              canDelete={false}
              isLoading={isLoading}
              denseTable
              verifyDelete
              tableTitle={"PM API Keys"}
              objectArray={formattedData}
              fieldTitles={fieldTitles}
              fieldTypes={fieldTypes}
              fieldNames={fieldNames}
              customChangePageRowsEvent={handleChangeRowsPerPage}
              customHandleChangePageEvent={handleChangePage}
              defaultItemCount={rowsPerPage}
              handlePreviewClickParentMethod={onEditKeyOpen}
              newItemCreationMethod={() => setCreateKeyModalOpen(true)}
              totalRowCount={totalCount}
            />

          </Grid>
        </Grid>
      </CardContent>
      <Dialog
        open={createKeyModalOpen}
        fullWidth={true}
        maxWidth={"lg"}
        keepMounted
        onClose={() => setCreateKeyModalOpen(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle id="alert-dialog-slide-title">Generate API Keys</DialogTitle>
        <DialogContent>
          <Card mb={6} elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Property Manager Selection */}
                <Box>
                  <FormControl
                    my={6}
                    fullWidth
                  >
                    <InputLabel id="selectedPMDropDownLabel">
                      Property Manager
                    </InputLabel>
                    {isUsersDataLoading ?
                      <Skeleton
                        variant="rect"
                        height="30px"
                        style={{backgroundColor: `#bdbfbe`, padding: 6, margin: 6}}
                      /> :
                      <Select
                        disabled={isUsersDataLoading}
                        labelId="selectedPMDropDownLabel"
                        id="selectedPMDropDown"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        value={selectedPM}
                        onChange={handlePmSelect}
                        fullWidth
                        my={2}
                        placeholder={'Select PM'}
                      >
                        {pmList && pmList?.map((item) => (
                          <MenuItem value={item.id} key={item.id}>
                            {item.firstName} {item.lastName}
                          </MenuItem>
                        ))}
                      </Select>
                    }
                  </FormControl>
                </Box>

                <Box>
                  {!Object.values(newApiKey).length ?
                    <h6 className="mb-6" style={{color: "red"}}>
                      Note! Previously requested API credentials will be deactivated.
                    </h6>
                    :
                    <>
                      <h6 className="mb-6" style={{color: "red"}}>
                        Make sure to save this information – you won't be able to view the API secret again.
                      </h6>
                      <Card className="card-box mb-4">
                        <List className="py-0">
                          <ListItem className="d-flex justify-content-end">
                            <Tooltip title={!copied ? "Copy" : "Copied"}>
                              <Button
                                color="primary"
                                onClick={copyToClipboard}
                              >
                                <ContentCopy fontSize="small"/>
                              </Button>
                            </Tooltip>
                          </ListItem>

                          <ListItem className="d-flex justify-content-between align-items-center">
                            Key:
                            <Badge color="primary">{newApiKey?.apiKey}</Badge>
                          </ListItem>
                          <ListItem className="d-flex justify-content-between align-items-center">
                            Secret:
                            <Badge color="secondary">{newApiKey?.secret}</Badge>
                          </ListItem>
                        </List>
                      </Card>
                    </>
                  }
                </Box>
              </Box>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
          {!Object.values(newApiKey).length ?
            <>
              <Button
                onClick={() => setCreateKeyModalOpen(false)}
                color="secondary"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                onClick={generateNewApiKey}
                variant="contained"
                color="primary"
                disabled={!selectedPM}
              >
                {isLoading ? (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                ) : 'Generate API Key'}

              </Button>
            </> :
            <Button
              onClick={handleCreateKeyModalClose}
              color="secondary"
              variant="contained"
            >
              Close
            </Button>
          }
        </DialogActions>

      </Dialog>
      <Dialog
        open={editKeyModalOpen}
        fullWidth={true}
        keepMounted
        onClose={()=> setEditKeyModalOpen(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle id="alert-dialog-slide-title">Manage API Keys</DialogTitle>
        {selectedRow?.status === "Active" ? <DialogContent>
          <DialogContentText style={{color: 'black'}}>
            Do you want to <strong>{editModalTitle}</strong> the keys?
          </DialogContentText>
            <DialogContentText style={{color: 'red'}}>*** This cannot be undone ***</DialogContentText>
            <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
            <Button
              onClick={() => setEditKeyModalOpen(false)}
              color="secondary"
              variant="contained"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirmDeactivate}
              variant="contained"
              color="primary"
            >
              {isLoading ? (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                ) :
                `Yes, ${editModalTitle || 'continue'}`}
            </Button>
          </DialogActions>
        </DialogContent> :
          <DialogContent>
            <DialogContentText style={{color: 'black'}}>The key cannot be <strong>{editModalTitle + 'd'}.</strong>
            </DialogContentText>
            <DialogContentText style={{color: 'black'}}>Please generate a new key/secret pair.</DialogContentText>
            <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
              <Button
                onClick={() => setEditKeyModalOpen(false)}
                color="secondary"
                variant="contained"
              >
                Close
              </Button>
            </DialogActions>
          </DialogContent>
        }
      </Dialog>
    </Card>
  );
};

export default ApiKeysTable;